import { useEffect, useRef, useState } from "react";
import { Button, Confirm, ErrorNote, Lightbox, Panel } from "../../components/ui";
import { SaveError, SaveStatus } from "../../components/save";
import { useSaveState } from "../../lib/saving";
import {
  listPhotos, uploadPhoto, deletePhoto, deleteAllPhotos, saveOrder, moveTo,
  photoUrl, zipAllPhotos, saveBlob, zipName, setPhotoPublic, type VehiclePhoto,
} from "./photos";

/**
 * The photos of one car.
 *
 * Files sit in a private bucket, so each image is fetched as the signed-in
 * user and turned into a local blob URL. Those URLs are revoked when the set
 * changes or the component unmounts — without that, every visit to a vehicle
 * would leak a few megabytes.
 *
 * Reordering is a drag, done with pointer events rather than HTML5 drag and
 * drop: the HTML5 API does not fire on touch at all, and this is mostly used
 * on a phone standing beside the car.
 *
 * The drag starts from a grip rather than the whole tile. A draggable tile
 * would need touch-action: none to stop the browser scrolling instead, and
 * with eighteen photos on screen that would make the page unscrollable.
 *
 * The move is tracked on the window, not on the grip, and that is the whole
 * reason this works. The first version captured the pointer to the grip
 * element. Reordering re-renders the grid, React moves that figure to its new
 * position in the DOM, and moving a captured element releases the capture —
 * so the drag died after exactly one tile and the pointerup that saves the
 * order never arrived. One bug, reported as two: "it only moves one space"
 * and "the cover never saves". The window does not get re-parented.
 *
 * Most reordering is really one job — make this the cover — so that has a
 * button of its own. Dragging a photo twelve tiles to the front on a phone is
 * not an interaction anyone should have to perform.
 */
export default function VehiclePhotos({ vehicleId, stockNo, vehicleName }: {
  vehicleId: string;
  /** Names the files inside the zip, so they still mean something once they
      are out of it. */
  stockNo: string;
  /** "2012 BMW X3", for naming the download. */
  vehicleName: string;
}) {
  const [photos, setPhotos] = useState<VehiclePhoto[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  /* Every change here — upload, order, cover, website, delete — happens on
     the tap rather than behind a Save button, so each one reports itself:
     "Saving…", then "Cover saved ✓ 3:04 PM" once the database has answered,
     or why it did not. */
  const photoSave = useSaveState();
  const [photoLabel, setPhotoLabel] = useState("Saved");
  const [photoHint, setPhotoHint] = useState<string | undefined>(undefined);
  const busy = photoSave.saving;
  const [uploading, setUploading] = useState(0);
  const [zipping, setZipping] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const owned = useRef<string[]>([]);

  /** Which photo is waiting to be confirmed. A photo is gone for good once
      the object is removed, so it is worth one tap to be sure. */
  const [confirmPhoto, setConfirmPhoto] = useState<VehiclePhoto | null>(null);
  /* Which photo is open full size. An index rather than the photo itself, so
     paging through with the arrows is just arithmetic. */
  const [viewing, setViewing] = useState<number | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  /* Dragging files over the panel. Counted rather than a boolean: dragging
     across a child element fires dragleave on the parent, and a boolean makes
     the highlight flicker as the pointer moves over the tiles. */
  const [dragDepth, setDragDepth] = useState(0);

  /** The photo being dragged. */
  const [dragId, setDragId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  /** The order when the drag began, so a failed save can be put back. */
  const beforeDrag = useRef<VehiclePhoto[]>([]);
  /**
   * The order as it stands right now.
   *
   * The drag ends on a window listener registered once per drag, which closes
   * over the state as it was when the drag started. Reading `photos` there
   * would save a stale order — the very thing being fixed — so the live array
   * is mirrored here and the save reads this.
   */
  const orderRef = useRef<VehiclePhoto[]>([]);
  orderRef.current = photos;

  useEffect(() => {
    let live = true;
    listPhotos(vehicleId)
      .then((rows) => { if (live) setPhotos(rows); })
      .catch((e) => setErr(e instanceof Error ? e.message : "Could not read the photos."));
    return () => { live = false; };
  }, [vehicleId]);

  // Fetch bytes for anything not already showing, and let go of the rest.
  useEffect(() => {
    let live = true;
    (async () => {
      for (const p of photos) {
        if (urls[p.id]) continue;
        try {
          const u = await photoUrl(p.storage_path);
          if (!live) { URL.revokeObjectURL(u); return; }
          owned.current.push(u);
          setUrls((m) => ({ ...m, [p.id]: u }));
        } catch {
          // One unreadable photo should not blank the whole set.
        }
      }
    })();
    return () => { live = false; };
  }, [photos, urls]);

  useEffect(() => () => {
    for (const u of owned.current) URL.revokeObjectURL(u);
    owned.current = [];
  }, []);

  /** Only the images. A folder or a PDF dropped by accident is ignored
      rather than failing halfway through the batch. */
  function imagesFrom(list: FileList | null): File[] {
    return Array.from(list ?? []).filter((f) => f.type.startsWith("image/"));
  }

  async function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragDepth(0);
    const files = imagesFrom(e.dataTransfer?.files ?? null);
    if (!files.length) {
      setErr("Those were not images.");
      return;
    }
    await upload(files);
  }

  async function onFiles(list: FileList | null) {
    const files = imagesFrom(list);
    if (!files.length) return;
    await upload(files);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function upload(files: File[]) {
    setErr(null);
    setUploading(files.length);
    setPhotoLabel(files.length === 1 ? "Photo uploaded" : `${files.length} photos uploaded`);
    setPhotoHint("Any photo shown above did upload; the rest did not. Add them again.");
    let order = photos.length;
    const added: VehiclePhoto[] = [];
    await photoSave.run(async () => {
      try {
        for (const file of files) {
          try {
            added.push(await uploadPhoto(vehicleId, file, order++));
          } finally {
            setUploading((n) => Math.max(0, n - 1));
          }
        }
      } finally {
        // Whatever made it is real and on the car, failure or not.
        if (added.length) setPhotos((p) => [...p, ...added]);
        setUploading(0);
      }
    }, { timeoutMs: 60_000 * files.length });
  }

  /* ---------------- dragging ---------------- */

  /** Which tile is under the pointer right now. */
  function indexUnder(x: number, y: number): number | null {
    const tiles = gridRef.current?.querySelectorAll<HTMLElement>("[data-index]");
    if (!tiles) return null;
    for (const tile of tiles) {
      const r = tile.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        return Number(tile.dataset.index);
      }
    }
    return null;
  }

  /** Write the order down, putting it back if the write fails. */
  async function commitOrder(next: VehiclePhoto[], previous: VehiclePhoto[], label = "Order saved") {
    const unchanged = previous.length === next.length
      && previous.every((p, i) => p.id === next[i]!.id);
    if (unchanged) return;

    setPhotoLabel(label);
    setPhotoHint("The photos are back in the order they were saved in.");
    const ok = await photoSave.run(() => saveOrder(next));
    // Put it back rather than leaving the screen and the database
    // disagreeing about which photo is the cover.
    if (!ok) setPhotos(previous);
  }

  function onGripDown(e: React.PointerEvent, id: string) {
    if (busy) return;
    e.preventDefault();
    beforeDrag.current = photos;
    setDragId(id);
    setErr(null);

    /* Listened for on the window rather than the grip. The grip is inside the
       figure being reordered, and a DOM node that moves loses its pointer
       capture along with every event that would have followed. */
    const move = (ev: PointerEvent) => {
      const over = indexUnder(ev.clientX, ev.clientY);
      if (over === null) return;
      ev.preventDefault();
      setPhotos((cur) => moveTo(cur, id, over));
    };

    const finish = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      setDragId(null);
      // orderRef, not `photos`: this closure was made when the drag began.
      void commitOrder(orderRef.current, beforeDrag.current);
    };

    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  }

  /** Send a photo to the front. The cover is the first one. */
  async function makeCover(id: string) {
    if (busy) return;
    const previous = photos;
    const next = moveTo(photos, id, 0);
    if (next === previous) return;
    setErr(null);
    setPhotos(next);
    await commitOrder(next, previous, "Cover saved");
  }

  /** Arrow keys on the grip: the same move without a mouse or a finger. */
  async function onGripKey(e: React.KeyboardEvent, id: string) {
    const delta = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
    if (!delta) return;
    e.preventDefault();
    const from = photos.findIndex((p) => p.id === id);
    const next = moveTo(photos, id, from + delta);
    if (next === photos) return;
    const previous = photos;
    setPhotos(next);
    await commitOrder(next, previous);
  }

  async function downloadAll() {
    setZipping(`0 of ${photos.length}`); setErr(null);
    try {
      const blob = await zipAllPhotos(photos, stockNo,
        (done, total) => setZipping(`${done} of ${total}`));
      /* "2012 BMW X3 720110.zip" rather than "720110-photos.zip". A folder
         of downloads sorts and reads by the car, which is how anyone looking
         for it later will be thinking about it. */
      saveBlob(blob, `${zipName(vehicleName, stockNo)}.zip`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not build the download.");
    } finally {
      setZipping(null);
    }
  }

  async function removeAll() {
    setErr(null);
    setPhotoLabel("All photos deleted");
    setPhotoHint("Some may have gone; the list above is what is still stored.");
    const ok = await photoSave.run(() => deleteAllPhotos(photos),
      { timeoutMs: 120_000 });
    setConfirmAll(false);
    if (ok) {
      for (const u of Object.values(urls)) URL.revokeObjectURL(u);
      owned.current = [];
      setUrls({});
      setPhotos([]);
    } else {
      // Some may have gone; read back rather than guessing what is left.
      await listPhotos(vehicleId).then(setPhotos).catch(() => {});
    }
  }

  async function remove(p: VehiclePhoto) {
    setErr(null);
    setPhotoLabel("Photo deleted");
    setPhotoHint("The photo is still there. Try again.");
    const ok = await photoSave.run(() => deletePhoto(p));
    setConfirmPhoto(null);
    if (!ok) return;
    setPhotos((rows) => rows.filter((r) => r.id !== p.id));
    setUrls((m) => {
      const u = m[p.id];
      if (u) URL.revokeObjectURL(u);
      const { [p.id]: _gone, ...rest } = m;
      return rest;
    });
  }

  /** Not optimistic: the label changes once the database has it. */
  async function togglePublic(p: VehiclePhoto) {
    const next = p.show_on_website === false;
    setErr(null);
    setPhotoLabel(next ? "Shown on the website" : "Hidden from the website");
    setPhotoHint("The photo's website setting has not changed.");
    const ok = await photoSave.run(() => setPhotoPublic(p.id, next));
    if (ok) {
      setPhotos((list) => list.map((x) =>
        x.id === p.id ? { ...x, show_on_website: next } : x));
    }
  }

  return (
    <Panel title="Photos" rail="record"
           note={photos.length ? `${photos.length}` : undefined}
           foot={
             <>
               <Button variant="ghost" onClick={() => fileInput.current?.click()}
                       disabled={uploading > 0}>
                 {uploading > 0 ? `Uploading ${uploading}` : "Add photos"}
               </Button>
               {/* No capture attribute. With capture="environment" iOS skips
                   the picker and opens the camera directly, so there is no way
                   to choose shots already taken. Without it the usual sheet
                   appears — Photo Library, Take Photo, Choose File — and
                   several can be selected at once. */}
               <input ref={fileInput} type="file" accept="image/*" multiple
                      style={{ display: "none" }}
                      onChange={(e) => onFiles(e.target.files)} />
               {photos.length > 0 && (
                 <Button variant="ghost" onClick={downloadAll}
                         disabled={zipping !== null || busy}>
                   {zipping ? `Preparing ${zipping}` : "Download all"}
                 </Button>
               )}
               {photos.length > 1 && (
                 <Button variant="danger" onClick={() => setConfirmAll(true)}
                         disabled={busy || zipping !== null}>
                   Remove all
                 </Button>
               )}
             </>
           }>
      <div
        className={`dropzone ${dragDepth > 0 ? "over" : ""}`}
        onDragEnter={(e) => { e.preventDefault(); setDragDepth((n) => n + 1); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setDragDepth((n) => Math.max(0, n - 1))}
        onDrop={(e) => void onDrop(e)}
      >
      {dragDepth > 0 && (
        <p className="dropnote">Drop the photos here</p>
      )}
      {photos.length === 0 ? (
        <p className="hint">
          No photos yet. Drag them in, or use the button below — on a phone
          that offers the camera and the photo library.
        </p>
      ) : (
        <div className="photogrid" ref={gridRef}>
          {photos.map((p, i) => (
            <figure className={`photo ${dragId === p.id ? "dragging" : ""}`}
                    key={p.id} data-index={i}>
              {i === 0
                ? <span className="photo-cover">Cover</span>
                : (
                  /* The reason most people started dragging in the first
                     place. Opposite the badge it replaces, so the eye learns
                     one spot for "which one is the cover". */
                  <button className="photo-makecover" disabled={busy}
                          title="Make this the cover photo"
                          onClick={() => void makeCover(p.id)}>
                    Make cover
                  </button>
                )}
              {urls[p.id]
                ? <img src={urls[p.id]} alt={p.caption ?? ""} loading="lazy"
                       draggable={false} className="photo-open"
                       onClick={() => setViewing(i)} />
                : <div className="photo-loading">loading</div>}
              <figcaption>
                <button className="grip" aria-label={`Move photo ${i + 1}`}
                        title="Drag to reorder, or use the arrow keys"
                        onPointerDown={(e) => onGripDown(e, p.id)}
                        onKeyDown={(e) => onGripKey(e, p.id)}>⣿</button>
                {/* Pictures of paperwork live in this gallery too, and the
                    website must not publish those. */}
                <button className={`linkbtn tiny ${p.show_on_website === false ? "off" : ""}`}
                        disabled={busy}
                        title={p.show_on_website === false
                          ? "Hidden from the website" : "Shown on the website"}
                        onClick={() => void togglePublic(p)}>
                  {p.show_on_website === false ? "hidden" : "on site"}
                </button>
                <button className="linkbtn tiny" disabled={busy}
                        onClick={() => setConfirmPhoto(p)}>remove</button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      </div>
      <p className="hint dim">
        Drop photos anywhere on this panel to add them. The first photo is the
        cover — hover any other photo and press <strong>Make cover</strong> to
        move it there.
        To order the rest, drag a photo by its grip, or focus the grip and use
        the arrow keys.
        Photos are shrunk to 1600px before upload — full camera files would
        fill the storage tier within months and nothing here shows larger.
      </p>
      {viewing !== null && photos[viewing] && urls[photos[viewing]!.id] && (
        <Lightbox
          src={urls[photos[viewing]!.id]!}
          caption={photos[viewing]!.caption ?? undefined}
          index={viewing} count={photos.length}
          onPrev={() => setViewing((v) => ((v ?? 0) - 1 + photos.length) % photos.length)}
          onNext={() => setViewing((v) => ((v ?? 0) + 1) % photos.length)}
          onClose={() => setViewing(null)}
        />
      )}

      {confirmAll && (
        <Confirm
          message={`Delete all ${photos.length} photos of this car? `
                 + "This cannot be undone. Download them first if you want to keep them."}
          confirmLabel={busy ? "Deleting" : `Delete all ${photos.length}`}
          onConfirm={removeAll}
          onCancel={() => setConfirmAll(false)}
        />
      )}

      {confirmPhoto && (
        <Confirm
          message={
            photos[0]?.id === confirmPhoto.id && photos.length > 1
              ? "Delete the cover photo? The next one becomes the cover. This cannot be undone."
              : "Delete this photo? This cannot be undone."
          }
          confirmLabel={busy ? "Deleting" : "Delete photo"}
          onConfirm={() => remove(confirmPhoto)}
          onCancel={() => setConfirmPhoto(null)}
        />
      )}
      {err && <ErrorNote>{err}</ErrorNote>}
      <p className="savenote">
        <SaveStatus save={photoSave} hideWhenIdle savedLabel={photoLabel} />
      </p>
      <SaveError save={photoSave} hint={photoHint} />
    </Panel>
  );
}
